import { capitalizeTerm } from "@loot-os/api-contract";
import { LootBoxOpener } from "@loot-os/react";
import { Badge } from "@loot-os/ui/components/badge";
import { BoxDropTable } from "@loot-os/ui/components/box-drop-table";
import { BoxSummaryLine } from "@loot-os/ui/components/box-summary-line";
import { buttonVariants } from "@loot-os/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { analyticsErrorCode, track } from "../lib/analytics.js";
import { useCurrency } from "../lib/currency.js";
import { boxQueryOptions, invalidateAfterClaim, invalidateAfterOpen } from "../lib/queries.js";
import { pendingRevealFromError, useRevealClaim } from "../lib/revealClaim.js";
import { useShopper } from "../lib/shopper.js";
import { useTenant } from "../lib/tenant.js";
import { useTerminology } from "../lib/terminology.js";

/**
 * GET /v1/boxes/:id + the embeddable opener. Signed in, opens persist via the
 * Loot session token inherited from <LootProvider>; signed out the opener
 * falls back to demo mode (local rolls, nothing saved). While the auth check
 * is still running neither is known: the opener shows its loading state
 * (`sessionPending` on <LootProvider>) and the demo banner stays hidden, so a
 * signed-in shopper never rolls a demo open before their session is minted.
 */
export function BoxDetailPage() {
  const { boxId } = useParams({ from: "/boxes/$boxId" });
  const { user, authPending, sessionToken } = useShopper();
  const { hasFeature, config } = useTenant();
  const terms = useTerminology();
  const currency = useCurrency();
  const unit = currency.namePlural;
  const { promptClaim } = useRevealClaim();
  const [openError, setOpenError] = useState<ReactNode | null>(null);

  const query = useQuery({ ...boxQueryOptions(boxId ?? "none"), enabled: !!boxId });
  const box = query.data ?? null;
  const error = query.error
    ? query.error instanceof Error
      ? query.error.message
      : `failed to load ${terms.box.singular}`
    : null;

  if (!boxId) return null;
  if (error) return <ErrorBanner>{error}</ErrorBanner>;

  return (
    <div className="grid gap-6">
      {box ? (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{box.name}</h1>
            <BoxSummaryLine
              priceCents={box.priceCents}
              openedCount={box.openedCount}
              currency={currency}
            />
            {box.forge && (
              <p className="text-xs text-muted-foreground">
                Forged by{" "}
                <span className="font-medium text-foreground">{box.forge.creatorDisplayName}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {user && hasFeature("battles") && (
              // Signed in only: the create page bounces a signed-out shopper to /login and the
              // magic link lands on /, so the box would be lost on the way.
              <Link
                to="/battles/create"
                search={{ boxId: box.id }}
                className={buttonVariants({ variant: "secondary", size: "sm" })}
              >
                {capitalizeTerm(terms.battle.singular)} with this {terms.box.singular}
              </Link>
            )}
            <Badge variant="outline" className="font-mono text-[11px]">
              v{box.version}
            </Badge>
          </div>
        </div>
      ) : (
        <Skeleton className="h-14 w-72" />
      )}

      {!authPending && !user && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm">
          <span>
            You're in <strong>demo mode</strong> — spins roll locally with the real odds, but
            nothing persists.
          </span>
          <Link to="/login" className={buttonVariants({ size: "sm" })}>
            Sign in to open for real
          </Link>
        </div>
      )}

      {openError && <ErrorBanner>{openError}</ErrorBanner>}

      <LootBoxOpener
        boxId={boxId}
        showFairness={false}
        // Returns undefined: recorded, never vetoed. Signed out, the opener rolls a demo open.
        onOpenStart={(quantity) => {
          track("open_clicked", { boxId, quantity, demo: !sessionToken });
          return undefined;
        }}
        onOpened={() => {
          setOpenError(null);
          invalidateAfterOpen();
        }}
        // Reveal mode: the balance moves at claim (or sell-back), not at open.
        onClaimed={() => {
          setOpenError(null);
          invalidateAfterClaim();
        }}
        onSoldBack={() => {
          setOpenError(null);
          invalidateAfterClaim();
        }}
        onInsufficientBalance={() => {
          track("insufficient_balance", { boxId, action: "open" });
          setOpenError(
            hasFeature("balancePurchases")
              ? `Not enough ${unit} — buy more from the Account page.`
              : `Not enough ${unit}.`,
          );
        }}
        onError={(err) => {
          // Any unclaimed reveal (a prior box reveal or a battle win) blocks opening; the claim
          // modal names and claims whichever it is, then the shopper can open again.
          const hint = pendingRevealFromError(err);
          if (hint) {
            setOpenError(null);
            promptClaim(hint);
            return;
          }
          // The opener also calls onInsufficientBalance for that code, which records it.
          const code = analyticsErrorCode(err);
          if (code !== "insufficient_balance") track("open_failed", { boxId, code });
          setOpenError(err instanceof Error ? err.message : "open failed");
        }}
      />

      {user && hasFeature("balancePurchases") && (
        <p className="text-xs text-muted-foreground">
          Short on {unit}?{" "}
          <Link className="underline" to="/account">
            Buy {unit}
          </Link>
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">What's inside — full odds</CardTitle>
        </CardHeader>
        <CardContent>
          {box ? (
            <BoxDropTable
              // An out-of-stock entry stays in the table at weight 0 so a roll verifies by
              // index; it cannot drop, so it is not listed.
              items={box.dropTable.filter((item) => item.weight > 0)}
              priceCents={box.priceCents}
              currency={currency}
              // The tenant's per-tier colors reach the opener's rails through
              // LootTheme; pass them here too so a tier reads the same in both.
              rarityColors={config.theme?.rarity}
            />
          ) : (
            <Skeleton className="h-48 w-full" />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
